import { createSignal, createMemo, onMount } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { fontFamily, isRTL, t } from "../translations/configuration";

// ── Layout constants ──────────────────────────────────────────────────────────
const COLS = 6;
const KEY_SIZE = 58;
const KEY_GAP = 8;
const KB_W = COLS * KEY_SIZE + (COLS - 1) * KEY_GAP;
const HALF_W = (KB_W - KEY_GAP) / 2;
const THIRD_W = (KB_W - 2 * KEY_GAP) / 3;

const CHARS = "abcdefghijklmnopqrstuvwxyz1234567890".split("");
const ARABIC_CHARS = "ابتثجحخدذرزسشصضطظعغفقكلمنهوي01234567".split("");
const SYMBOLS = ".,-_+=!?@#$%&*()/:;'\"<>~^\\|[]{}€£¥§ ".split("");
const ROWS_COUNT = CHARS.length / COLS;  // 6

const PREVIEW_H = 66;
const SPECIAL0_Y = PREVIEW_H + KEY_GAP;
const CHARS_Y = SPECIAL0_Y + KEY_SIZE + KEY_GAP + 10;
const SPECIAL1_Y = CHARS_Y + ROWS_COUNT * (KEY_SIZE + KEY_GAP);

// ── Component ─────────────────────────────────────────────────────────────────
const KbPanel = (props: {
  value: () => string;
  fieldLabel: () => string;
  onChar: (c: string) => void;
  onBackspace: () => void;
  onDone: () => void;
  hidePreview?: boolean;
  kbRef?: (ref: { setFocus: () => void }) => void;
}) => {
  const [symbols, setSymbols] = createSignal(false);
  const [caps, setCaps] = createSignal(false);
  const chars = createMemo(() => {
    const base = symbols() ? SYMBOLS : (isRTL() ? ARABIC_CHARS : CHARS);
    return caps() && !symbols() ? base.map(ch => ch.toUpperCase()) : base;
  });
  const [focusedKey, setFocusedKey] = createSignal<{ r: number; c: number } | null>(null);
  const [isSpaceFocused, setIsSpaceFocused] = createSignal(false);
  const [isBackFocused, setIsBackFocused] = createSignal(false);
  const [isCapsFocused, setIsCapsFocused] = createSignal(false);
  const [isSymbolsFocused, setIsSymbolsFocused] = createSignal(false);
  const [isDoneFocused, setIsDoneFocused] = createSignal(false);

  const kbRefs: any[][] = Array.from({ length: ROWS_COUNT }, () => []);
  let spaceRef: any, backRef: any, capsRef: any, symbolsRef: any, doneRef: any;

  const yOff = props.hidePreview ? -(PREVIEW_H + KEY_GAP) : 0;

  onMount(() => {
    if (props.kbRef) props.kbRef({ setFocus: () => kbRefs[0]?.[0]?.setFocus() });
    setTimeout(() => kbRefs[0]?.[0]?.setFocus(), 50);
  });

  const isFocused = (r: number, c: number) => () =>
    focusedKey()?.r === r && focusedKey()?.c === c;

  const goUp = (r: number, c: number) => {
    if (r === 0) {
      if (c < COLS / 2) spaceRef?.setFocus();
      else backRef?.setFocus();
    } else {
      kbRefs[r - 1]?.[c]?.setFocus();
    }
  };

  const goDown = (r: number, c: number) => {
    if (r < ROWS_COUNT - 1) {
      kbRefs[r + 1]?.[c]?.setFocus();
    } else {
      if (c < COLS / 3) capsRef?.setFocus();
      else if (c < (COLS / 3) * 2) symbolsRef?.setFocus();
      else doneRef?.setFocus();
    }
  };

  return (
    <View width={KB_W} x={30}>

      {/* ── Field preview (hidden when hidePreview=true) ── */}
      {!props.hidePreview && (
        <View x={0} y={0} width={KB_W} height={PREVIEW_H} borderRadius={12} color={hexColor("#0d1117")}>
          <Text x={14} y={6}
            style={{ fontSize: 14, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
            {props.fieldLabel()}
          </Text>
          <Text x={14} y={28}
            style={{ fontSize: 22, color: hexColor(props.value() ? "#f0f6fc" : "#374151"), fontFamily: fontFamily() }}>
            {props.value() ? props.value() + "|" : "Type here..."}
          </Text>
        </View>
      )}

      {/* ── Top row: SPACE | BACKSPACE ── */}
      <View
        ref={(el: any) => { spaceRef = el; }}
        x={0} y={SPECIAL0_Y + 10 + yOff} width={HALF_W} height={KEY_SIZE} borderRadius={6}
        style={{ color: hexColor("#202329"), focus: { color: hexColor("#ffffff") }, transition: { color: { duration: 120 } } }}
        onFocus={() => setIsSpaceFocused(true)}
        onBlur={() => setIsSpaceFocused(false)}
        onEnter={() => props.onChar(" ")}
        onLeft={() => isRTL() ? props.onDone() : undefined}
        onRight={() => backRef?.setFocus()}
        onDown={() => kbRefs[0]?.[0]?.setFocus()}
      >
        <Text
          x={Math.round((HALF_W - 50) / 2)} y={Math.round((KEY_SIZE - 15) / 2)}
          color={hexColor(isSpaceFocused() ? "#000000" : "#ffffff")}
          style={{ fontSize: 18, fontFamily: fontFamily() }}
        >{t("kbSpace")}</Text>
      </View>

      <View
        ref={(el: any) => { backRef = el; }}
        x={HALF_W + KEY_GAP} y={SPECIAL0_Y + 10 + yOff} width={HALF_W} height={KEY_SIZE} borderRadius={6}
        style={{ color: hexColor("#374151"), focus: { color: hexColor("#ffffff") }, transition: { color: { duration: 120 } } }}
        onFocus={() => setIsBackFocused(true)}
        onBlur={() => setIsBackFocused(false)}
        onEnter={props.onBackspace}
        onLeft={() => spaceRef?.setFocus()}
        onUp={props.onDone}
        onRight={() => isRTL() ? undefined : props.onDone()}
        onDown={() => kbRefs[0]?.[COLS - 1]?.setFocus()}
      >
        <View
          x={Math.round((HALF_W - 28) / 2)} y={Math.round((KEY_SIZE - 28) / 2)}
          width={28} height={28}
          src="https://i.ibb.co/wh6n5N3X/backspace.png"
          color={hexColor(isBackFocused() ? "#000000" : "#ffffff")}
        />
      </View>

      {/* ── Character grid (6 × 6) ── */}
      {Array.from({ length: ROWS_COUNT }, (_, r) =>
        Array.from({ length: COLS }, (_, c) => {
          return (
            <View
              ref={(el: any) => { kbRefs[r][c] = el; }}
              x={c * (KEY_SIZE + KEY_GAP)}
              y={CHARS_Y + r * (KEY_SIZE + KEY_GAP) + yOff}
              width={KEY_SIZE} height={KEY_SIZE}
              borderRadius={6}
              style={{ color: hexColor("#202329"), focus: { color: hexColor("#ffffff") }, transition: { color: { duration: 120 } } }}
              onFocus={() => setFocusedKey({ r, c })}
              onEnter={() => props.onChar(chars()[r * COLS + c])}
              onLeft={() => c > 0 ? kbRefs[r][c - 1]?.setFocus() : (isRTL() ? props.onDone() : undefined)}
              onRight={() => c < COLS - 1 ? kbRefs[r][c + 1]?.setFocus() : (isRTL() ? undefined : props.onDone())}
              onUp={() => goUp(r, c)}
              onDown={() => goDown(r, c)}
            >
              <Text
                x={Math.round((KEY_SIZE - 16) / 2)}
                y={Math.round((KEY_SIZE - 26) / 2)}
                color={hexColor(isFocused(r, c)() ? "#000000" : "#ffffff")}
                style={{ fontSize: 24, fontFamily: fontFamily() }}
              >
                {chars()[r * COLS + c]}
              </Text>
            </View>
          );
        })
      )}

      {/* ── Bottom row: CAPS | ABC/#+= | DONE ── */}
      <View
        ref={(el: any) => { capsRef = el; }}
        x={0} y={SPECIAL1_Y + yOff} width={THIRD_W} height={KEY_SIZE} borderRadius={6}
        style={{ color: hexColor(caps() ? "#4b5563" : "#202329"), focus: { color: hexColor("#ffffff") }, transition: { color: { duration: 120 } } }}
        onFocus={() => setIsCapsFocused(true)}
        onBlur={() => setIsCapsFocused(false)}
        onEnter={() => setCaps(v => !v)}
        onLeft={() => isRTL() ? props.onDone() : undefined}
        onRight={() => symbolsRef?.setFocus()}
        onUp={() => kbRefs[ROWS_COUNT - 1]?.[0]?.setFocus()}
        onDown={() => { }}
      >
        <Text
          x={Math.round((THIRD_W - 50) / 2)} y={Math.round((KEY_SIZE - 15) / 2)}
          color={hexColor(isCapsFocused() ? "#000000" : caps() ? "#60a5fa" : "#ffffff")}
          style={{ fontSize: 16, fontWeight: caps() ? "bold" : "normal", fontFamily: fontFamily() }}
        >CAPS</Text>
      </View>

      <View
        ref={(el: any) => { symbolsRef = el; }}
        x={THIRD_W + KEY_GAP} y={SPECIAL1_Y + yOff} width={THIRD_W} height={KEY_SIZE} borderRadius={6}
        style={{ color: hexColor(symbols() ? "#4b5563" : "#202329"), focus: { color: hexColor("#ffffff") }, transition: { color: { duration: 120 } } }}
        onFocus={() => setIsSymbolsFocused(true)}
        onBlur={() => setIsSymbolsFocused(false)}
        onEnter={() => setSymbols(v => !v)}
        onLeft={() => capsRef?.setFocus()}
        onRight={() => doneRef?.setFocus()}
        onUp={() => kbRefs[ROWS_COUNT - 1]?.[Math.floor(COLS / 2)]?.setFocus()}
        onDown={() => { }}
      >
        <Text
          x={Math.round((THIRD_W - 45) / 2)} y={Math.round((KEY_SIZE - 15) / 2)}
          color={hexColor(isSymbolsFocused() ? "#000000" : symbols() ? "#60a5fa" : "#ffffff")}
          style={{ fontSize: 18, fontWeight: symbols() ? "bold" : "normal", fontFamily: fontFamily() }}
        >{symbols() ? "ABC" : "#+="}</Text>
      </View>

      <View
        ref={(el: any) => { doneRef = el; }}
        x={2 * (THIRD_W + KEY_GAP)} y={SPECIAL1_Y + yOff} width={THIRD_W} height={KEY_SIZE} borderRadius={6}
        style={{ color: hexColor("#1e3a8a"), focus: { color: hexColor("#2563eb") }, transition: { color: { duration: 120 } } }}
        onFocus={() => setIsDoneFocused(true)}
        onBlur={() => setIsDoneFocused(false)}
        onEnter={props.onDone}
        onLeft={() => symbolsRef?.setFocus()}
        onUp={() => kbRefs[ROWS_COUNT - 1]?.[COLS - 1]?.setFocus()}
        onDown={() => { }}
      >
        <Text
          x={Math.round((THIRD_W - 45) / 2)} y={Math.round((KEY_SIZE - 15) / 2)}
          color={hexColor(isDoneFocused() ? "#000000" : "#ffffff")}
          style={{ fontSize: 18, fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}
        >DONE</Text>
      </View>

    </View>
  );
};

export default KbPanel;
